import { useEffect, useRef } from 'react';

interface HeroProps {
  onOpenModal: () => void;
}

export default function Hero({ onOpenModal }: HeroProps) {
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Minimal animation — just reveal once, no heavy loops
    const timer = requestAnimationFrame(() => {
      if (!textRef.current) return;
      const items = textRef.current.querySelectorAll<HTMLElement>('.hero-item');
      items.forEach((item, i) => {
        setTimeout(() => {
          item.style.opacity = '1';
          item.style.transform = 'translateY(0)';
        }, i * 100);
      });
    });
    return () => cancelAnimationFrame(timer);
  }, []);

  return (
    <section className="relative min-h-[560px] md:min-h-screen flex items-center overflow-hidden" aria-labelledby="hero-heading">
      {/* Background Image — eager load for LCP */}
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src="https://public.readdy.ai/ai/img_res/edited_6110afa674ce1ee19e2df9324a55aa93_d611940b.jpg"
          alt=""
          loading="eager"
          decoding="sync"
          fetchPriority="high"
          width="1440"
          height="900"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/35 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
      </div>

      {/* Text-first Content — critical for LCP */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-6 py-24 md:py-32">
        <div ref={textRef} className="max-w-xl md:max-w-2xl">
          {/* Label */}
          <div
            className="hero-item inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 mb-5"
            style={{ opacity: 0, transform: 'translateY(12px)', transition: 'opacity 0.4s ease, transform 0.4s ease' }}
          >
            <i className="ri-map-pin-2-line text-[#C8D8C0] text-sm" aria-hidden="true" />
            <span className="text-white/90 text-xs font-medium tracking-wide">鳥取県特化｜はじめての家づくりガイド</span>
          </div>

          {/* Main Heading — H1 for LCP target */}
          <h1
            id="hero-heading"
            className="hero-item text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5"
            style={{ opacity: 0, transform: 'translateY(12px)', transition: 'opacity 0.4s ease, transform 0.4s ease' }}
          >
            鳥取で家を建てたい。<br />
            <span className="text-[#C8D8C0]">でも、何から始めればいい？</span>
          </h1>

          {/* Sub Copy */}
          <p
            className="hero-item text-base md:text-lg text-white/85 leading-relaxed mb-8 max-w-xl"
            style={{ opacity: 0, transform: 'translateY(12px)', transition: 'opacity 0.4s ease, transform 0.4s ease' }}
          >
            費用・会社選び・土地探し・契約まで、鳥取の家づくりを<br className="hidden md:block" />
            専門知識ゼロでも理解できるようにわかりやすく解説します。
          </p>

          {/* Trust Badges */}
          <div
            className="hero-item flex flex-wrap items-center gap-3 mb-7"
            style={{ opacity: 0, transform: 'translateY(12px)', transition: 'opacity 0.4s ease, transform 0.4s ease' }}
          >
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/25">
              <i className="ri-article-line text-[#C8D8C0] text-sm" aria-hidden="true" />
              <span className="text-white/90 text-xs font-medium">記事数 53本</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/25">
              <i className="ri-map-pin-line text-[#C8D8C0] text-sm" aria-hidden="true" />
              <span className="text-white/90 text-xs font-medium">鳥取市・米子市・倉吉市 対応</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/25">
              <i className="ri-shield-check-line text-[#C8D8C0] text-sm" aria-hidden="true" />
              <span className="text-white/90 text-xs font-medium">中立・公正な情報</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div
            className="hero-item flex flex-col sm:flex-row gap-3"
            style={{ opacity: 0, transform: 'translateY(12px)', transition: 'opacity 0.4s ease, transform 0.4s ease' }}
          >
            <a
              href="#entry-action"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#8FA882] text-white font-semibold text-sm hover:bg-[#7A9570] transition-colors duration-200 cursor-pointer text-center leading-snug"
            >
              <i className="ri-book-open-line flex-shrink-0" aria-hidden="true" />
              <span>初心者はまずここから<br className="sm:hidden" />｜家づくりの基礎をまとめて理解する→</span>
            </a>
            <button
              type="button"
              onClick={onOpenModal}
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#D4A96A] text-white font-semibold text-sm md:text-base hover:bg-[#C09050] transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-list-3-line" aria-hidden="true" />
              資料請求はこちら（無料）
            </button>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/60" aria-hidden="true">
        <span className="text-[10px] font-medium tracking-widest">SCROLL</span>
        <div className="w-px h-7 bg-white/40 overflow-hidden">
          <div className="w-full bg-white/70 h-1/2 animate-[bounce_1.5s_ease-in-out_infinite]" />
        </div>
      </div>
    </section>
  );
}
